<script setup>
// Astra DS · PlayerDesplegable — lo que suena, desplegado bajo la cabecera.
//
// Figma `Pattern/PlayerDesplegable 2585:1060`, tablero `2587:33024`. Entrega
// `2026-10-21-portada-franja-y-player-en-cabecera.md`, aprobada por Pablo el
// 21.10.26.
//
//   portada 362 · EN EMISIÓN + programa · artista y canción · álbum · progreso
//
// Justo bajo el filete de la cabecera, alineado con el borde izquierdo de la
// portada del player y con su ancho, 402. **Sin borde, sin radio, sin sombra y
// sin velo**: fondo `bg/elevated`, 20 a los lados y arriba, 22 abajo y 16 entre
// bloques. Sin animación. Solo escritorio. Quien lo abre y lo cierra es
// `SiteHeader`; esto solo lo pinta.
import { computed } from "vue";
import DuotoneArt from "./DuotoneArt.vue";
import MiniWave from "./MiniWave.vue";
import SignalDot from "./SignalDot.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  open: { type: Boolean, default: false },
  // Dónde: el borde izquierdo de la portada y el pie de la cabecera, en px.
  izquierda: { type: Number, default: 0 },
  techo: { type: Number, default: 46 },
  // { portada, artista, cancion, album, programa, franja, transcurrido, duracion }
  sonando: { type: Object, default: () => ({}) },
  playing: { type: Boolean, default: false },
  levels: { type: Array, default: () => [] },
  analisis: { type: String, default: "esperando" },
});

function reloj(segundos) {
  const t = Math.max(0, Math.floor(Number(segundos) || 0));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

const programa = computed(() =>
  [props.sonando.programa, props.sonando.franja].filter(Boolean).join(" · "),
);

const avance = computed(() => {
  const d = Number(props.sonando.duracion) || 0;
  return d ? Math.min(100, (Number(props.sonando.transcurrido) || 0) / d * 100) : 0;
});
</script>

<template>
  <section
    v-if="open"
    id="player-desplegable"
    class="desplegable"
    :style="{ left: `${izquierda}px`, top: `${techo}px` }"
    :aria-label="t('marco.emision.loQueSuenaTitulo')"
  >
    <!-- 1. La portada, en cuadrado de lado a lado. 🔴 Siempre en su color, suene
         o no (Pablo, 21.10.26): es la del disco, y el disco no va en gris. -->
    <DuotoneArt
      class="desplegable__portada"
      :src="sonando.portada || ''"
      :alt="sonando.album || sonando.artista || ''"
      :duotone="false"
      :hover-color="false"
      ratio="1"
      position="center"
    />

    <!-- 2. Onda + EN EMISIÓN y el programa -->
    <p class="desplegable__estado">
      <MiniWave v-if="analisis !== 'no'" :levels="levels" :playing="playing" />
      <SignalDot v-else :playing="playing" />
      <span class="desplegable__emision">{{ t("marco.emision.enEmision") }}</span>
      <span v-if="programa" class="desplegable__programa">{{ programa }}</span>
    </p>

    <!-- 3. Artista y canción -->
    <div class="desplegable__cancion">
      <p v-if="sonando.artista" class="desplegable__artista">{{ sonando.artista }}</p>
      <p v-if="sonando.cancion" class="desplegable__titulo">{{ sonando.cancion }}</p>
    </div>

    <!-- 4. El álbum. Sin álbum, este bloque no va. -->
    <div v-if="sonando.album" class="desplegable__album">
      <p class="desplegable__rotulo">{{ t("marco.emision.album") }}</p>
      <p class="desplegable__album-nombre">{{ sonando.album }}</p>
    </div>

    <!-- 5. El progreso: transcurrido · pista · duración -->
    <div class="desplegable__progreso">
      <span>{{ reloj(sonando.transcurrido) }}</span>
      <span class="desplegable__pista" aria-hidden="true">
        <i :style="{ width: `${avance}%` }" />
      </span>
      <span>{{ reloj(sonando.duracion) }}</span>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.desplegable {
  position: fixed;
  z-index: 90;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  width: 402px;
  padding: 20px 20px 22px;
  background: var(--color-bg-elevated);
}

// 1. 362 × 362: el ancho menos los 20 de cada lado.
.desplegable__portada {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: var(--color-art-mid);

  :deep(.duotone) {
    height: 100%;
  }
}

// 2. `mono/micro`: EN EMISIÓN en acento y el programa en terciario, a 10.
.desplegable__estado {
  @include t.mono-micro;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: var(--color-text-accent);
  text-transform: uppercase;
  white-space: nowrap;
}

.desplegable__emision {
  color: var(--color-text-accent);
}

.desplegable__programa {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text-tertiary);
}

// 3. El artista en `display/section` y, a 10, la canción en `mono/lead`.
.desplegable__cancion {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.desplegable__artista {
  @include t.display-section;
  margin: 0;
  color: var(--color-text-primary);
  overflow-wrap: anywhere;
}

.desplegable__titulo {
  @include t.mono-lead;
  margin: 0;
  color: var(--color-text-primary);
  overflow-wrap: anywhere;
}

// 4. Filete arriba, a 14 ÁLBUM en `mono/nano` y, a 6, el nombre en `mono/label`.
.desplegable__album {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 14px;
  border-top: 1px solid var(--color-border-default);
}

.desplegable__rotulo {
  @include t.mono-nano;
  margin: 0;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
}

.desplegable__album-nombre {
  @include t.mono-label;
  margin: 0;
  color: var(--color-text-primary);
  text-transform: uppercase;
}

// 5. Transcurrido y duración en `mono/micro` `text/secondary` y entre los dos, a
// 12, la pista de 2 en `border/default` con el avance en el rojo de acción.
.desplegable__progreso {
  @include t.mono-micro;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
}

.desplegable__pista {
  position: relative;
  flex: 1 1 0;
  height: 2px;
  background: var(--color-border-default);

  i {
    position: absolute;
    inset: 0 auto 0 0;
    background: var(--color-action-primary-default);
  }
}
</style>
